"use client";

import { useEffect, useState } from "react";
import {
  filterAvailableFixedDateCampaigns,
  isFixedDateVoucher,
  isFixedDateVoucherUnavailable,
  millisecondsUntilAmsterdamDateChange,
  resolveDealDateFields,
} from "@/lib/fixed-date-voucher.mjs";
import {
  hasVoucherSalesEnd,
  isVoucherSaleClosed,
  withoutClosedVouchers,
} from "@/app/(marketing)/_branded-voucher/voucher-landing-config";

const MAX_TIMEOUT_MS = 2_147_000_000;

/** Shared reactive Amsterdam business clock for client-side catalogue gates. */
export function useAmsterdamBusinessDateClock(enabled = true) {
  const [clock, setClock] = useState(() => new Date());

  useEffect(() => {
    if (!enabled) return undefined;

    let timeoutId;
    let disposed = false;
    const refreshAndSchedule = () => {
      if (disposed) return;
      const now = new Date();
      setClock(now);
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(
        refreshAndSchedule,
        Math.min(
          millisecondsUntilAmsterdamDateChange(now) + 25,
          MAX_TIMEOUT_MS,
        ),
      );
    };
    const refreshVisibleTab = () => {
      if (document.visibilityState === "visible") refreshAndSchedule();
    };

    refreshAndSchedule();
    window.addEventListener("focus", refreshAndSchedule);
    document.addEventListener("visibilitychange", refreshVisibleTab);
    return () => {
      disposed = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener("focus", refreshAndSchedule);
      document.removeEventListener("visibilitychange", refreshVisibleTab);
    };
  }, [enabled]);

  return clock;
}

function needsFixedDateAvailabilityClock(deal) {
  const dateFields = resolveDealDateFields(deal);
  return (
    isFixedDateVoucher(deal) ||
    dateFields.hasDateFieldConflict ||
    hasVoucherSalesEnd(deal)
  );
}

/**
 * Filter one or more catalogue collections against one shared business-clock
 * snapshot. A category page supplies both its deal and voucher arrays here so
 * cards, wrappers, counts and facets all close in the same React render.
 * Fixronde 3 #1920: the same clock drops a voucher past its last sales day
 * (182, salesEndDate), so a page rendered or cached before midnight loses the
 * card at midnight without a new server render.
 */
export function useAvailableFixedDateCampaignCollections(...collections) {
  const normalizedCollections = collections.map((rows) =>
    Array.isArray(rows) ? rows : [],
  );
  const shouldTrack = normalizedCollections.some((rows) =>
    rows.some(needsFixedDateAvailabilityClock),
  );
  const clock = useAmsterdamBusinessDateClock(shouldTrack);

  return normalizedCollections.map((rows) =>
    withoutClosedVouchers(filterAvailableFixedDateCampaigns(rows, clock), clock),
  );
}

/**
 * Fixronde 3 #1920: reactive client gate for one card. True once the voucher
 * is past its last sales day (isVoucherSaleClosed), re-checked at Amsterdam
 * midnight and when a background tab is focused again.
 */
export function useVoucherSaleClosed(deal) {
  const clock = useAmsterdamBusinessDateClock(hasVoucherSalesEnd(deal));
  return isVoucherSaleClosed(deal?.deals_id ?? deal?.dealId ?? deal?.id, clock);
}

/**
 * Reactive client counterpart of the server-side fixed-campaign gate.
 *
 * A page can stay open across Amsterdam midnight. Re-render exactly when the
 * business date changes, and refresh immediately when a background tab is
 * focused again, so calendars disappear without waiting for navigation.
 */
export function useFixedDateVoucherUnavailable(deal) {
  const dateFields = resolveDealDateFields(deal);
  const shouldTrack =
    isFixedDateVoucher(deal) || dateFields.hasTravelDateFieldConflict;
  const clock = useAmsterdamBusinessDateClock(shouldTrack);

  return isFixedDateVoucherUnavailable(deal, clock);
}
